import { OrderStatusResponse } from "@dana/Types/ApiModels";
import React from "react";
import translate from "@insite/client-framework/Translate";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import styled, { css } from "styled-components";
import injectCss from "@insite/mobius/utilities/injectCss";
import { formatDateWithSlash } from "@dana/Common/utils";
import Link from "@insite/mobius/Link";

interface ShippingTrackingProps {
    trackingData: OrderStatusResponse | null;
}

export interface ShippingTrackingStyles {
    trackingContainer?: InjectableCss;
}

export const shippingTrackingStyles: ShippingTrackingStyles = {
    trackingContainer: {
        css: css`
            padding: 16px;           
            margin: 0 auto;
            font-family: inherit;

           /* Desktop / tablet landscape: grid acts like a table */
.trackingTable {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.trackingTableRow {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr 1fr 1fr 1.4fr;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--border-color, #e5e5e5);
    align-items: center;
}

.trackingTableHeaderRow {
    font-size: 12px;    
    color: var(--text-secondary, #6b655c);
    font-weight: 600;
}

.trackingTableCell {
    font-size: 14px;
    overflow-wrap: anywhere;
}

.trackingTableCell--empty {
    grid-column: 3 / -1;
    color: var(--text-secondary, #6b655c);
    font-style: italic;
}

/* Tablet: tighten columns a bit */
@media (max-width: 900px) {
    .trackingTableRow {
        grid-template-columns: 1fr 0.7fr 1fr 1fr;
        font-size: 13px;
    }
    .trackingTableCell:nth-child(5) {
        grid-column: 1 / -1; /* tracking number drops to its own line */
    }
}

/* Mobile: collapse to stacked label/value cards */
@media (max-width: 600px) {
    .trackingTableHead {
        display: none; /* labels move inline via data-label instead */
    }

    .trackingTableRow {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px 12px;
        padding: 12px;
        border: 1px solid var(--border-color, #e5e5e5);
        border-radius: 8px;
        margin-bottom: 8px;
    }

    .trackingTableCell {
        display: flex;
        flex-direction: column;
    }

    .trackingTableCell::before {
        content: attr(data-label);
        font-size: 11px;      
        color: var(--text-secondary, #6b655c);
        margin-bottom: 2px;
    }

    .trackingTableCell--empty {
        grid-column: 1 / -1;
    }

    /* Tracking number is the most important field on mobile — give it its own full-width row */
    .trackingTableCell[data-label="Tracking Number"] {
        grid-column: 1 / -1;
    }
}
        `,
    },
};

const StyledContainer = styled.div`
    ${injectCss}
`;

const ShippingTracking: React.FC<ShippingTrackingProps> = ({ trackingData }) => {
    if (!trackingData?.items?.length) {
        return null;
    }

    return (
        <StyledContainer {...shippingTrackingStyles.trackingContainer}>
           <div className="itemList">
    <div className="trackingTable" role="table">
        <div className="trackingTableHead" role="rowgroup">
            <div className="trackingTableRow trackingTableHeaderRow" role="row">
                <span className="trackingTableCell" role="columnheader">{translate("Part No.")}</span>
                <span className="trackingTableCell" role="columnheader">{translate("Qty Shipped")}</span>
                <span className="trackingTableCell" role="columnheader">{translate("Date Shipped")}</span>
                <span className="trackingTableCell" role="columnheader">{translate("Carrier")}</span>
                <span className="trackingTableCell" role="columnheader">{translate("Tracking Number")}</span>
            </div>
        </div>

        <div className="trackingTableBody" role="rowgroup">
            {trackingData.items.map((item, itemIndex) => {
                const validLines = item.schLines?.filter((track) => track.trackingNumber !== null) ?? [];

                if (validLines.length === 0) {
                    return (
                        <div className="trackingTableRow" role="row" key={`${item.danaPartNumber}-${itemIndex}`}>
                            <span className="trackingTableCell" data-label={translate("Part No.")}>{item.danaPartNumber}</span>
                            <span className="trackingTableCell" data-label={translate("Qty Shipped")}>{item.quantityShipped}</span>
                            <span className="trackingTableCell trackingTableCell--empty" data-label={translate("Status")}>
                                {translate("Not yet shipped")}
                            </span>
                        </div>
                    );
                }

                return validLines.map((track, trackIndex) => (
                    <div className="trackingTableRow" role="row" key={`${item.danaPartNumber}-${track.trackingNumber}-${trackIndex}`}>
                        <span className="trackingTableCell" data-label={translate("Part No.")}>
                            {item.danaPartNumber}
                        </span>
                        <span className="trackingTableCell" data-label={translate("Qty Shipped")}>
                            {track.actualShipQuantity}
                        </span>
                        <span className="trackingTableCell" data-label={translate("Date Shipped")}>
                            {formatDateWithSlash(track.actualShipDate?.toString()?.split('T')[0] || "")}
                        </span>
                        <span className="trackingTableCell" data-label={translate("Carrier")}>
                            {track.carrierNumber}
                        </span>
                        <span className="trackingTableCell" data-label={translate("Tracking Number")}>
                        {track.trackingNumber.split('♠')[1] ? (
                        <Link target="_blank" href={`${track.trackingNumber.split('♠')[1]}`}>
                            {track.trackingNumber.split('♠')[0]}
                        </Link>
                        ) : (
                        <span>{track.trackingNumber}</span>
                        )}
                        </span>
                    </div>
                ));
            })}
        </div>
    </div>
</div>
        </StyledContainer>
    );
};

export default ShippingTracking;